import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Phone, Search } from 'lucide-react';
import { Input } from './input';
import { Button } from './button';
import { ScrollArea } from './scroll-area';

// Popular country codes (sorted by relevance for African market)
const COUNTRY_CODES = [
  { code: '+234', country: 'Nigeria', flag: '🇳🇬', abbr: 'NG', id: 'NG' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭', abbr: 'GH', id: 'GH' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪', abbr: 'KE', id: 'KE' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦', abbr: 'ZA', id: 'ZA' },
  { code: '+256', country: 'Uganda', flag: '🇺🇬', abbr: 'UG', id: 'UG' },
  { code: '+255', country: 'Tanzania', flag: '🇹🇿', abbr: 'TZ', id: 'TZ' },
  { code: '+237', country: 'Cameroon', flag: '🇨🇲', abbr: 'CM', id: 'CM' },
  { code: '+225', country: 'Ivory Coast', flag: '🇨🇮', abbr: 'CI', id: 'CI' },
  { code: '+221', country: 'Senegal', flag: '🇸🇳', abbr: 'SN', id: 'SN' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬', abbr: 'EG', id: 'EG' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦', abbr: 'MA', id: 'MA' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿', abbr: 'DZ', id: 'DZ' },
  { code: '+251', country: 'Ethiopia', flag: '🇪🇹', abbr: 'ET', id: 'ET' },
  { code: '+260', country: 'Zambia', flag: '🇿🇲', abbr: 'ZM', id: 'ZM' },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼', abbr: 'ZW', id: 'ZW' },
  // North America
  { code: '+1', country: 'Canada', flag: '🇨🇦', abbr: 'CA', id: 'CA' },
  { code: '+1', country: 'United States', flag: '🇺🇸', abbr: 'US', id: 'US' },
  // International
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧', abbr: 'GB', id: 'GB' },
  { code: '+91', country: 'India', flag: '🇮🇳', abbr: 'IN', id: 'IN' },
  { code: '+86', country: 'China', flag: '🇨🇳', abbr: 'CN', id: 'CN' },
  { code: '+971', country: 'UAE', flag: '🇦🇪', abbr: 'AE', id: 'AE' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', abbr: 'SA', id: 'SA' },
  { code: '+49', country: 'Germany', flag: '🇩🇪', abbr: 'DE', id: 'DE' },
  { code: '+33', country: 'France', flag: '🇫🇷', abbr: 'FR', id: 'FR' },
  { code: '+39', country: 'Italy', flag: '🇮🇹', abbr: 'IT', id: 'IT' },
  { code: '+34', country: 'Spain', flag: '🇪🇸', abbr: 'ES', id: 'ES' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷', abbr: 'BR', id: 'BR' },
  { code: '+81', country: 'Japan', flag: '🇯🇵', abbr: 'JP', id: 'JP' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷', abbr: 'KR', id: 'KR' },
  { code: '+61', country: 'Australia', flag: '🇦🇺', abbr: 'AU', id: 'AU' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿', abbr: 'NZ', id: 'NZ' },
];

export function PhoneInput({
  value = '',
  countryCode = '+234',
  onValueChange,
  onCountryCodeChange,
  placeholder = 'Phone number',
  disabled = false,
  required = false,
  className = '',
  'data-testid': testId = 'phone-input'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];

  const filteredCountries = COUNTRY_CODES.filter(country =>
    country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.code.includes(searchQuery) ||
    country.abbr.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    // Use both mousedown and touchstart for iOS compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleCountrySelect = (country) => {
    onCountryCodeChange?.(country.code);
    setIsOpen(false);
    setSearchQuery('');
  };

  const formatPhoneNumber = (input) => {
    // Remove non-digit characters
    const digits = input.replace(/\D/g, '');
    return digits;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    onValueChange?.(formatted);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex">
        {/* Country Code Selector */}
        <Button
          type="button"
          variant="outline"
          className="h-12 px-3 rounded-l-xl rounded-r-none border-r-0 bg-muted/50 hover:bg-muted flex items-center gap-1 min-w-[90px]"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          data-testid={`${testId}-country-selector`}
        >
          <span className="text-lg">{selectedCountry.flag}</span>
          <span className="text-sm font-medium">{selectedCountry.code}</span>
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </Button>

        {/* Phone Number Input */}
        <div className="relative flex-1">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="tel"
            value={value}
            onChange={handlePhoneChange}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            className="h-12 pl-10 rounded-l-none rounded-r-xl border-l-0 text-base"
            style={{ fontSize: '16px' }}
            data-testid={`${testId}-number`}
          />
        </div>
      </div>

      {/* Country Code Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-xl shadow-lg overflow-hidden">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search country..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 pl-9"
                autoFocus
                data-testid={`${testId}-country-search`}
              />
            </div>
          </div>

          {/* Country List */}
          <ScrollArea className="h-[240px]">
            <div className="p-1">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <button
                    key={country.id}
                    type="button"
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-left ${
                      country.code === countryCode ? 'bg-primary/10 text-primary' : ''
                    }`}
                    onClick={() => handleCountrySelect(country)}
                    data-testid={`${testId}-country-${country.abbr}`}
                  >
                    <span className="text-xl">{country.flag}</span>
                    <span className="flex-1 text-sm">{country.country}</span>
                    <span className="text-sm text-muted-foreground">{country.code}</span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No countries found
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

export { COUNTRY_CODES };
